import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';
import { sendEmail, sendReminderEmail } from '../../services/resendemailService';

const { FiBookOpen, FiUserPlus, FiTarget, FiRefreshCw, FiBell, FiCheck, FiX, FiLoader } = FiIcons;

const actions = [
  { label: 'Assign Training', icon: FiBookOpen, color: 'bg-blue-600 hover:bg-blue-700 text-white', key: 'assign' },
  { label: 'Enroll in Campaign', icon: FiUserPlus, color: 'bg-violet-600 hover:bg-violet-700 text-white', key: 'enroll' },
  { label: 'Schedule Phishing Test', icon: FiTarget, color: 'bg-orange-500 hover:bg-orange-600 text-white', key: 'phishing' },
  { label: 'Mark for Retraining', icon: FiRefreshCw, color: 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200', key: 'retrain' },
  { label: 'Send Reminder', icon: FiBell, color: 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200', key: 'remind' },
];

const ProfileActions = ({ learner, onUpdate }) => {
  const [triggered, setTriggered] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [resources, setResources] = useState({ modules: [], campaigns: [] });
  const [selection, setSelection] = useState('');
  const [simulationName, setSimulationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [error, setError] = useState('');

  const handleAction = (key) => {
    setTriggered(key);
    setTimeout(() => setTriggered(null), 2000);
    setActiveAction(key);
    setSelection('');
    setSimulationName(`${learner?.firstName || ''} ${learner?.lastName || ''} Security Check`.trim());
    setError('');

    if (key === 'assign' || key === 'enroll' || key === 'retrain') {
      loadResources();
    }
  };

  const loadResources = async () => {
    setLoadingResources(true);

    const [modulesRes, campaignsRes] = await Promise.all([
      dataFetch('read/modules?limit=500'),
      dataFetch('read/campaigns?limit=500')
    ]);

    setResources({
      modules: modulesRes?.status === 'success' ? modulesRes?.data ?? [] : [],
      campaigns: campaignsRes?.status === 'success' ? campaignsRes?.data ?? [] : []
    });
    setLoadingResources(false);
  };

  const getArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return String(value).split(',').map(item => item.trim()).filter(Boolean);
    }
  };

  const createAssignment = async (moduleId, campaignId = '') => {
    return dataFetch('create/assignments', {
      method: 'POST',
      body: JSON.stringify({
        module_id: String(moduleId),
        employee_id: String(learner?.id),
        status: 'Not Started',
        score: 0,
        user_id: localStorage.getItem('user_id') || '',
        campaign_id: String(campaignId),
        year: new Date().getFullYear(),
        attempts: 0,
        time_spent: 0
      })
    });
  };

  const completeAction = () => {
    setActiveAction(null);
    onUpdate?.();
  };

  const submitAction = async () => {
    setLoading(true);
    setError('');

    if (activeAction === 'assign' || activeAction === 'retrain') {
      if (!selection) {
        setError('Select a training module.');
        setLoading(false);
        return;
      }

      const assignmentRes = await createAssignment(selection);

      if (assignmentRes?.status !== 'success') {
        setError(assignmentRes?.error || 'Unable to assign training.');
        setLoading(false);
        return;
      }

      if (activeAction === 'retrain') {
        const employeeRes = await dataFetch(`update/employees/${learner?.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            trainingStatus: 'In Progress',
            user_id: localStorage.getItem('user_id') || ''
          })
        });

        if (employeeRes?.status !== 'success') {
          setError(employeeRes?.error || 'Training assigned, but learner status could not be updated.');
          setLoading(false);
          return;
        }
      }

      completeAction();
    }

    if (activeAction === 'enroll') {
      const campaign = resources.campaigns.find(item => String(item?.id) === String(selection));

      if (!campaign) {
        setError('Select a campaign.');
        setLoading(false);
        return;
      }

      const learnerIds = getArray(campaign?.assignedLearners).map(String);
      const updatedLearners = [...new Set([...learnerIds, String(learner?.id)])];
      const campaignRes = await dataFetch(`update/campaigns/${campaign?.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          assignedLearners: JSON.stringify(updatedLearners),
          user_id: localStorage.getItem('user_id') || ''
        })
      });

      if (campaignRes?.status !== 'success') {
        setError(campaignRes?.error || 'Unable to enroll learner in this campaign.');
        setLoading(false);
        return;
      }

      const assignmentResults = await Promise.all(
        getArray(campaign?.trainingContent).map(moduleId => createAssignment(moduleId, campaign?.id))
      );

      if (assignmentResults.some(result => result?.status !== 'success')) {
        setError('Learner enrolled, but one or more training assignments could not be created.');
        setLoading(false);
        return;
      }

      completeAction();
    }

    if (activeAction === 'phishing') {
      if (!simulationName.trim()) {
        setError('Enter a simulation name.');
        setLoading(false);
        return;
      }

      const userId = localStorage.getItem('user_id') || '';
      const simulationRes = await dataFetch('create/phishing_simulations', {
        method: 'POST',
        body: JSON.stringify({
          name: simulationName.trim(),
          date: new Date().toISOString().slice(0, 19).replace('T', ' '),
          type: 'Password Expiry Notice',
          sent: 1,
          opened: 0,
          clicked: 0,
          reported: 0,
          user_id: userId,
          training_module_id: '',
          recipient_type: 'Specific',
          target_audience: 'Specific'
        })
      });

      if (simulationRes?.status !== 'success') {
        setError(simulationRes?.error || 'Unable to schedule phishing test.');
        setLoading(false);
        return;
      }

      const recipientRes = await dataFetch('create/phishing_recipients', {
        method: 'POST',
        body: JSON.stringify({
          campaign_id: String(simulationRes?.data?.id ?? ''),
          employee_id: String(learner?.id),
          email: learner?.email ?? '',
          tracking_token: '',
          status: 'Sent',
          clicked: 0,
          user_id: userId
        })
      });

      if (recipientRes?.status !== 'success') {
        setError(recipientRes?.error || 'Simulation was created, but learner tracking could not be saved.');
        setLoading(false);
        return;
      }

      await sendEmail({
        toName: `${learner?.firstName || ''} ${learner?.lastName || ''}`.trim(),
        toEmail: learner?.email || '',
        subject: 'Security awareness test',
        message: 'You have been selected for a security awareness exercise. Please follow the instructions in the simulation email.'
      });

      completeAction();
    }

    if (activeAction === 'remind') {
      if (!learner?.email) {
        setError('This learner does not have an email address.');
        setLoading(false);
        return;
      }

      const loginUrl = `${window.location.origin}/login`;
      const emailRes = await sendReminderEmail({
        toName: `${learner?.firstName || ''} ${learner?.lastName || ''}`.trim(),
        toEmail: learner.email,
        subject: 'Training reminder',
        message: `This is a reminder to complete your assigned security awareness training as soon as possible. Log in here: ${loginUrl}`,
        html: `<p>This is a reminder to complete your assigned security awareness training as soon as possible.</p><p><a href="${loginUrl}">Log in to your account</a></p>`
      });

      if (!emailRes?.success) {
        setError(emailRes?.error || emailRes?.message || 'Unable to send reminder through Resend.');
        setLoading(false);
        return;
      }

      await dataFetch('create/audit_logs', {
        method: 'POST',
        body: JSON.stringify({
          action: 'Training reminder sent',
          details: `Reminder sent to ${learner.email}`,
          user_id: localStorage.getItem('user_id') || '',
          created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
        })
      });

      completeAction();
    }

    setLoading(false);
  };

  const getModalTitle = () => actions.find(action => action.key === activeAction)?.label || '';

  const renderActionFields = () => {
    if (loadingResources) {
      return (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
          <SafeIcon icon={FiLoader} className="animate-spin" /> Loading options...
        </div>
      );
    }

    if (activeAction === 'assign' || activeAction === 'retrain') {
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {activeAction === 'retrain' ? 'Remedial Training Module' : 'Training Module'}
          </label>
          <select value={selection} onChange={event => setSelection(event.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white">
            <option value="">Select a module...</option>
            {resources.modules.filter(module => module?.status !== 'Draft').map(module => (
              <option key={module?.id} value={module?.id}>{module?.title || 'Untitled module'}</option>
            ))}
          </select>
        </div>
      );
    }

    if (activeAction === 'enroll') {
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Active Campaign</label>
          <select value={selection} onChange={event => setSelection(event.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 bg-white">
            <option value="">Select a campaign...</option>
            {resources.campaigns.filter(campaign => campaign?.status !== 'Completed').map(campaign => (
              <option key={campaign?.id} value={campaign?.id}>{campaign?.campaignName || 'Untitled campaign'}</option>
            ))}
          </select>
        </div>
      );
    }

    if (activeAction === 'phishing') {
      return (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Simulation Name</label>
          <input value={simulationName} onChange={event => setSimulationName(event.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500" />
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
        A training reminder will be sent to <strong>{learner?.email || 'this learner'}</strong>.
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Admin Actions</p>
        <div className="flex flex-wrap gap-2">
          {actions.map(a => (
            <motion.button
              key={a.key}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleAction(a.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${a.color}`}
            >
              <SafeIcon icon={triggered === a.key ? FiCheck : a.icon} className="text-base" />
              {triggered === a.key ? 'Done!' : a.label}
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeAction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={loading ? undefined : () => setActiveAction(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 96 }}
              className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">{getModalTitle()}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{learner?.firstName} {learner?.lastName}</p>
                </div>
                <button onClick={() => setActiveAction(null)} disabled={loading} className="text-slate-400 hover:text-slate-600 disabled:opacity-50">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                {renderActionFields()}
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <button onClick={() => setActiveAction(null)} disabled={loading}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={submitAction} disabled={loading || loadingResources}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
                  <SafeIcon icon={loading ? FiLoader : FiCheck} className={loading ? 'animate-spin' : ''} />
                  {loading ? 'Saving...' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProfileActions;